import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useBilling } from "../context/BillingContext";
import PackPreviewSheet from "./PackPreviewSheet";

/**
 * 6 vertically-stacked pack cards matching the Group Mode reference photo.
 * Each card: large emoji on left, neon accent, name + tagline, chevron arrow.
 * Tap a card → API start → caller advances to gameplay (next session).
 */
const PACKS = [
  {
    id: "mlt-mega",        title: "Most Likely To", emoji: "💀",
    tagline: "85 prompts — chaotic, sweet, spicy & party — all shuffled.",
    accent: "#FFB800", accentBg: "rgba(255, 184, 0, 0.16)",
    tag: "FAN FAVE", tier: "free",
  },
  {
    id: "confessions",     title: "Confessions",     emoji: "🤫",
    tagline: "Type your truth. Group reacts. Anonymous + public.",
    accent: "#FF2E9A", accentBg: "rgba(255, 46, 154, 0.16)",
    tag: "JUICY", tier: "premium",
  },
  {
    id: "lowkey-toxic",    title: "Red Flag Radar",  emoji: "🚩",
    tagline: "Spot the energy nobody wants to admit.",
    accent: "#FF8A00", accentBg: "rgba(255, 184, 0, 0.16)",
    tag: "BOLD", tier: "premium",
  },
  {
    id: "chaos-mode",      title: "Chaos Mode",      emoji: "🤣",
    tagline: "Unhinged prompts. No survivors. No regrets.",
    accent: "#FF6B47", accentBg: "rgba(255, 107, 71, 0.16)",
    tag: "WILD", tier: "free",
  },
];

function FloatingParticles({ count = 18 }) {
  const items = Array.from({ length: count }, (_, i) => ({
    left: (i * 11 + 5) % 100,
    size: 1.4 + (i % 4) * 0.7,
    delay: (i * 0.55) % 8,
    dur: 11 + (i % 6) * 1.4,
    color: i % 3 === 0 ? "#FF8A00" : i % 3 === 1 ? "#FFE082" : "#FF8A00",
  }));
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
      {items.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute", top: 0, left: `${p.left}%`,
            width: p.size, height: p.size, borderRadius: "50%",
            background: p.color, boxShadow: `0 0 ${p.size * 4}px ${p.color}`,
            opacity: 0.42,
            animation: `vcDriftUp ${p.dur}s linear infinite`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes vcDriftUp {
          0% { transform: translateY(110vh); opacity: 0; }
          15% { opacity: 0.85; }
          85% { opacity: 0.85; }
          100% { transform: translateY(-12vh); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function PlayGroupPickPack({ code, onBack, onLaunched, mode = "start", currentPackId = null }) {
  const { isPremium, isPackUnlocked, openPaywall } = useBilling();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const isSwitch = mode === "switch";

  const handlePick = async (p) => {
    if (loading || selected) return;
    if (isSwitch && p.id === currentPackId) {
      toast.info("That's the pack you're already playing");
      return;
    }
    // Premium-locked → preview sheet instead of starting the room
    if (p.tier === "premium" && !isPremium && !isPackUnlocked(p.id)) {
      setPreviewing(p);
      return;
    }
    setSelected(p.id);
    setLoading(true);
    try {
      const room = isSwitch
        ? await api.switchGroupPack(code, { pack_id: p.id })
        : await api.startGroupRoom(code, { pack_id: p.id });
      // Brief visual confirmation, then advance to gameplay (next phase)
      setTimeout(() => onLaunched?.(room), 350);
    } catch (e) {
      setSelected(null);
      setLoading(false);
      toast.error(e?.response?.data?.detail || (isSwitch ? "Couldn't switch packs" : "Couldn't start the round"));
    }
  };

  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="group-pickpack-screen">
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          top: 60, left: "50%", transform: "translateX(-50%)",
          width: 460, height: 460,
          background: "radial-gradient(circle, rgba(255, 138, 0, 0.28) 0%, rgba(255, 184, 0, 0.12) 38%, transparent 70%)",
          filter: "blur(50px)", zIndex: 0,
        }}
        aria-hidden
      />
      <FloatingParticles count={20} />

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-5 pb-12 min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={onBack}
            aria-label="Back"
            data-testid="group-pickpack-back"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white/85 hover:text-white transition active:scale-95"
            style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.10)", backdropFilter: "blur(8px)" }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div
            className="px-3 py-1 rounded-full text-[10px] font-black tracking-[0.22em]"
            style={{
              background: "rgba(255, 138, 0, 0.10)",
              border: "1.5px solid #FF8A00",
              color: "#FFE082",
              boxShadow: "0 0 10px rgba(255, 138, 0, 0.45)",
            }}
          >
            {isSwitch ? "🔄 SWITCH PACK" : "🎉 GROUP MODE"}
          </div>
          <div className="w-10" aria-hidden />
        </div>

        {/* Title */}
        <div className="text-center mb-5 mt-2">
          <h1
            className="font-extrabold leading-[0.95] tracking-tight mb-2 inline-flex items-center gap-2"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(1.95rem, 8vw, 2.5rem)",
              letterSpacing: "-0.03em",
            }}
            data-testid="group-pickpack-title"
          >
            <span className="vc-title vc-title-gold">
              {isSwitch ? "Switch pack" : "Pick a pack"}
            </span>
            <motion.span
              animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.12, 1] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              style={{ filter: "drop-shadow(0 0 6px rgba(255, 138, 0, 0.7))" }}
            >
              {isSwitch ? "🔄" : "✨"}
            </motion.span>
          </h1>
          <p className="text-white/70 text-[13.5px] font-medium">
            {isSwitch
              ? "Pick a new vibe — your squad stays put."
              : "Tap a pack to lock it in for the squad. No takebacks."}
          </p>
        </div>

        {/* Cards */}
        <div className="space-y-2.5" data-testid="group-pickpack-list">
          {PACKS.map((p, idx) => {
            const sel = selected === p.id;
            const isPremium = p.tier === "premium";
            const isCurrent = isSwitch && currentPackId === p.id;
            return (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 + idx * 0.04, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => handlePick(p)}
                disabled={loading}
                data-testid={`group-pickpack-card-${p.id}`}
                whileTap={{ scale: 0.985 }}
                className="w-full rounded-2xl px-4 py-3.5 flex items-center gap-3.5 text-left transition disabled:opacity-60 relative"
                style={{
                  background: `linear-gradient(110deg, ${p.accentBg} 0%, rgba(10, 6, 10, 0.78) 100%)`,
                  border: `1.5px solid ${sel ? p.accent : `${p.accent}55`}`,
                  boxShadow: sel
                    ? `0 0 26px ${p.accent}bb, inset 0 0 22px ${p.accent}26`
                    : `0 0 12px ${p.accent}3a, inset 0 0 14px ${p.accent}10`,
                  backdropFilter: "blur(12px)",
                }}
              >
                {/* Emoji column */}
                <div
                  className="flex-shrink-0 w-[64px] h-[64px] rounded-2xl flex items-center justify-center"
                  style={{
                    background: `${p.accent}1f`,
                    border: `1.5px solid ${p.accent}80`,
                    boxShadow: `0 0 14px ${p.accent}66, inset 0 0 10px ${p.accent}22`,
                  }}
                >
                  <span className="text-[36px]" style={{ filter: `drop-shadow(0 0 8px ${p.accent})` }}>
                    {p.emoji}
                  </span>
                </div>

                {/* Text column */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <h3
                      className="text-white text-[16px] font-extrabold leading-tight"
                      style={{ letterSpacing: "-0.01em", fontFamily: "'Manrope', sans-serif" }}
                    >
                      {p.title}
                    </h3>
                    <span
                      className="text-[9.5px] font-black tracking-[0.16em] px-2 py-[2px] rounded-md"
                      style={{
                        background: p.accentBg,
                        color: p.accent,
                        border: `1px solid ${p.accent}80`,
                      }}
                    >
                      {p.tag}
                    </span>
                    <span
                      className="inline-flex items-center gap-1 text-[9px] font-black tracking-[0.1em] px-2 py-[2px] rounded-full uppercase"
                      style={
                        isPremium
                          ? {
                              background: "linear-gradient(135deg, #FF2E9A, #FF6B47)",
                              color: "#FFFFFF",
                              border: "1px solid rgba(255, 255, 255, 0.32)",
                              boxShadow: "0 0 10px rgba(255, 46, 154, 0.6)",
                            }
                          : {
                              background: "rgba(61, 214, 140, 0.16)",
                              color: "#7DE6A9",
                              border: "1px solid rgba(61, 214, 140, 0.6)",
                              boxShadow: "0 0 6px rgba(61, 214, 140, 0.4)",
                            }
                      }
                      data-testid={`group-pickpack-tier-${p.id}`}
                    >
                      {isPremium ? "💎 Premium" : "✓ Free"}
                    </span>
                    {isCurrent && (
                      <span
                        className="inline-flex items-center text-[9px] font-black tracking-[0.14em] px-2 py-[2px] rounded-full uppercase"
                        style={{
                          background: "rgba(255, 184, 0, 0.18)",
                          color: "#FFE082",
                          border: "1px solid rgba(255, 184, 0, 0.7)",
                          boxShadow: "0 0 8px rgba(255, 184, 0, 0.5)",
                        }}
                        data-testid={`group-pickpack-current-${p.id}`}
                      >
                        Now Playing
                      </span>
                    )}
                  </div>
                  <p className="text-[#C8C5D8] text-[12px] leading-snug">{p.tagline}</p>
                </div>

                {/* Chevron / loader */}
                {sel && loading ? (
                  <Loader2 className="w-5 h-5 animate-spin flex-shrink-0" style={{ color: p.accent }} />
                ) : (
                  <ArrowRight className="w-5 h-5 flex-shrink-0" style={{ color: p.accent, filter: `drop-shadow(0 0 4px ${p.accent})` }} strokeWidth={2.4} />
                )}
              </motion.button>
            );
          })}
        </div>
      </main>

      <PackPreviewSheet
        pack={previewing}
        onClose={() => setPreviewing(null)}
        onUnlock={() => {
          const id = previewing?.id;
          setPreviewing(null);
          openPaywall("pack_locked", id);
        }}
      />
    </div>
  );
}
